import { useEffect, useRef, useState } from 'react'

const SLIDE_DURATION_MS = 8000

interface UseHeroCarouselTimerProps {
  slideCount: number
  isHovering: boolean
  onSlideChange: () => void
}

interface UseHeroCarouselTimerReturn {
  timeRemaining: number
  resetTimer: () => void
}

/**
 * Custom hook that manages slideshow timer with intelligent hover state handling
 *
 * Behavior:
 * - Slide lasts 8 seconds when not hovering
 * - When hovering, timer pauses and video plays
 * - When stop hovering, timer resumes with remaining time
 * - Manual slide changes reset the timer
 *
 * @param slideCount Number of slides in carousel
 * @param isHovering Whether user is currently hovering
 * @param onSlideChange Callback when timer expires or video ends
 * @returns Timer state and reset function
 */
export let useHeroCarouselTimer = ({
  slideCount,
  isHovering,
  onSlideChange,
}: UseHeroCarouselTimerProps): UseHeroCarouselTimerReturn => {
  let [timeRemaining, setTimeRemaining] = useState(SLIDE_DURATION_MS)

  // Refs to track timer and state without causing re-renders
  let timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  let timeRemainingRef = useRef(SLIDE_DURATION_MS)

  let resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      // eslint-disable-next-line fp/no-mutation
      timerRef.current = null
    }
    // eslint-disable-next-line fp/no-mutation
    timeRemainingRef.current = SLIDE_DURATION_MS
    setTimeRemaining(SLIDE_DURATION_MS)
  }

  // Main timer effect - handles all timing logic
  useEffect(() => {
    // Early exit if only one slide
    if (slideCount <= 1) {
      return undefined
    }

    // Clean up previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      // eslint-disable-next-line fp/no-mutation
      timerRef.current = null
    }

    // If hovering, don't start a timer
    if (isHovering) {
      return undefined
    }

    // If we just stopped hovering, preserve the remaining time and continue
    let startTime = Date.now()
    let timeToWait = timeRemainingRef.current

    let handleTimerTick = () => {
      let elapsedSinceStart = Date.now() - startTime
      let newTimeRemaining = timeToWait - elapsedSinceStart

      if (newTimeRemaining <= 0) {
        // Timer expired, advance to next slide
        resetTimer()
        onSlideChange()
      } else {
        // Update remaining time state (for UI indicators if needed)
        // eslint-disable-next-line fp/no-mutation
        timeRemainingRef.current = newTimeRemaining
        setTimeRemaining(newTimeRemaining)

        // Schedule next tick
        let nextTimerId = setTimeout(handleTimerTick, 16) // ~60fps
        // eslint-disable-next-line fp/no-mutation
        timerRef.current = nextTimerId
      }
    }

    let initialTimerId = setTimeout(handleTimerTick, 16)
    // eslint-disable-next-line fp/no-mutation
    timerRef.current = initialTimerId

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        // eslint-disable-next-line fp/no-mutation
        timerRef.current = null
      }
    }
  }, [slideCount, isHovering, onSlideChange, resetTimer])

  return {
    timeRemaining,
    resetTimer,
  }
}
